/**
 * Summary calculation utilities for Timeline header
 *
 * Functions to calculate mode-specific summary metrics
 */

import { DateHelper } from "@bryntum/schedulerpro";
import { ISwivuProject } from "@/types/SwivuProject";
import { Event } from "@/bryntum-lib/models/Event";
import { Assignment } from "@/bryntum-lib/models/Assignment";
import { EntityRecord, TimelineData, PlanningEvent, ReminderEvent } from "./types";

export interface ScheduledResourcesSummary {
  daysInvested: number;
  daysProjected: number;
  totalResources: number;
}

export interface StatusChangesSummary {
  totalChanges: number;
  lastChangeDate: string | null;
}

export interface PlanningEventsSummary {
  totalEvents: number;
  totalDaysPlanned: number;
}

export interface HistorySummary {
  totalEvents: number;
  lastActivityDate: string | null;
}

export interface RemindersSummary {
  totalReminders: number;
  upcomingReminders: number;
}

export type TimelineSummary = ScheduledResourcesSummary | StatusChangesSummary | PlanningEventsSummary | HistorySummary | RemindersSummary | null;

/**
 * Calculate Scheduled Resources summary for a task
 */
export const calculateScheduledResourcesSummary = (record: EntityRecord, project: ISwivuProject | null): ScheduledResourcesSummary | null => {
  if (!project || !("events" in record)) {
    return null;
  }

  const taskId = record.id;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get all events for this task (excluding archived)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const eventStore = (project as any).eventStore;
  const taskEvents = eventStore?.query((e: Event) => e.taskId === taskId && !e.archived, true) || [];

  // Get assignments for these events to find unique resources
  const eventIds = new Set(taskEvents.map((e: Event) => e.id));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const assignmentStore = (project as any).assignmentStore;
  const assignments = assignmentStore?.query((a: Assignment) => eventIds.has(a.eventId), true) || [];

  const uniqueResources = new Set(assignments.map((a: Assignment) => a.resourceId));

  // Calculate days invested and projected
  let daysInvested = 0;
  let daysProjected = 0;

  taskEvents.forEach((event: Event) => {
    if (!event.startDate || !event.endDate) return;

    const startDate = new Date(event.startDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(event.endDate);
    endDate.setHours(0, 0, 0, 0);

    // Calculate total days for the event (excluding endDate)
    // DateHelper.diff gives the number of days between dates, excluding the end date
    const totalDays = DateHelper.diff(startDate, endDate, "d");

    // Days Projected: Total days for the event (all scheduled days, excluding endDate)
    daysProjected += totalDays;

    // Days Invested: Count only days up to and including today (excluding endDate)
    if (startDate <= today) {
      // Event has started or is starting today
      // Since endDate is not counted, the last countable day is endDate - 1 day
      const lastCountableDay = DateHelper.add(endDate, -1, "day");
      // Count days from startDate to min(lastCountableDay, today), inclusive
      const effectiveEndDate = lastCountableDay <= today ? lastCountableDay : today;
      // DateHelper.diff excludes the end date, so we add 1 to include both start and end
      const investedDays = DateHelper.diff(startDate, effectiveEndDate, "d") + 1;
      daysInvested += investedDays;
    }
    // If event hasn't started (startDate > today), daysInvested remains 0 for this event
  });

  return {
    daysInvested: Math.round(daysInvested),
    daysProjected: Math.round(daysProjected),
    totalResources: uniqueResources.size
  };
};

/**
 * Calculate Status Changes summary
 */
export const calculateStatusChangesSummary = (timelineData: TimelineData): StatusChangesSummary | null => {
  const statusChanges = timelineData.statusChanges || [];

  if (statusChanges.length === 0) {
    return {
      totalChanges: 0,
      lastChangeDate: null
    };
  }

  // Sort by date descending to get most recent
  const sorted = [...statusChanges].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return {
    totalChanges: statusChanges.length,
    lastChangeDate: sorted[0].date
  };
};

/**
 * Calculate Planning Events summary
 */
export const calculatePlanningEventsSummary = (timelineData: TimelineData): PlanningEventsSummary | null => {
  const planningDates = timelineData.planningDates || [];

  if (planningDates.length === 0) {
    return {
      totalEvents: 0,
      totalDaysPlanned: 0
    };
  }

  let totalDaysPlanned = 0;

  planningDates.forEach((planning: PlanningEvent) => {
    if (planning.startDate && planning.endDate) {
      try {
        const start = new Date(planning.startDate);
        const end = new Date(planning.endDate);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          const days = DateHelper.diff(start, end, "d") + 1;
          totalDaysPlanned += days;
        }
      } catch (error) {
        console.warn("Error calculating planning days:", error);
      }
    }
  });

  return {
    totalEvents: planningDates.length,
    totalDaysPlanned: Math.round(totalDaysPlanned)
  };
};

/**
 * Calculate History summary
 */
export const calculateHistorySummary = (timelineData: TimelineData): HistorySummary | null => {
  const historyDates = timelineData.historyDates || [];

  if (historyDates.length === 0) {
    return {
      totalEvents: 0,
      lastActivityDate: null
    };
  }

  // Sort by date descending to get most recent
  const sorted = [...historyDates].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return {
    totalEvents: historyDates.length,
    lastActivityDate: sorted[0].date
  };
};

/**
 * Calculate Reminders summary
 */
export const calculateRemindersSummary = (timelineData: TimelineData): RemindersSummary | null => {
  const reminderDates = timelineData.reminderDates || [];

  if (reminderDates.length === 0) {
    return {
      totalReminders: 0,
      upcomingReminders: 0
    };
  }

  // Get current date and time (don't reset to midnight - we need precise time comparison)
  const now = new Date();

  const upcomingReminders = reminderDates.filter((reminder: ReminderEvent) => {
    if (!reminder.alertDate) return false;
    const alertDate = new Date(reminder.alertDate);
    // Compare full date and time - only count reminders that are in the future
    return alertDate > now;
  }).length;

  return {
    totalReminders: reminderDates.length,
    upcomingReminders
  };
};
